'use client';

import { useTranslations } from 'next-intl';
import { Users, TrendingUp, Zap, DollarSign } from 'lucide-react';
import ContactFormSection from '../contact/sections/ContactFromSection';

export default function TeamAugmentationContent() {
  const t = useTranslations('TeamAugmentation');

  return (
    <>
      {/* About CISCODE Section */}
      <section className="relative w-full py-16 md:py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 text-center">
              {t('about.title')}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed text-center max-w-3xl mx-auto">
              {t('about.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative w-full py-16 md:py-20 lg:py-24 bg-dark-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <a
              href="#contact"
              className="inline-block bg-gradient-to-r from-[#3F74EF] to-[#5B8FF9] text-white font-semibold px-8 py-4 hover:shadow-[0_8px_30px_rgba(63,116,239,0.4)] transition-all duration-300"
            >
              {t('hero.cta')}
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative w-full py-16 md:py-20 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
              {t('benefits.title')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  key: 'expertise',
                  icon: Users,
                  title: t('benefits.items.expertise.title'),
                  description: t('benefits.items.expertise.description'),
                },
                {
                  key: 'flexibility',
                  icon: TrendingUp,
                  title: t('benefits.items.flexibility.title'),
                  description: t('benefits.items.flexibility.description'),
                },
                {
                  key: 'speed',
                  icon: Zap,
                  title: t('benefits.items.speed.title'),
                  description: t('benefits.items.speed.description'),
                },
                {
                  key: 'cost',
                  icon: DollarSign,
                  title: t('benefits.items.cost.title'),
                  description: t('benefits.items.cost.description'),
                },
              ].map(({ key, icon: Icon, title, description }) => (
                <div
                  key={key}
                  className="bg-white border border-gray-200 p-8 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-dark-navy flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                      <p className="text-gray-600 leading-relaxed">{description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative w-full pt-16 md:pt-20 lg:pt-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('contact.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('contact.description')}
            </p>
          </div>

          {/* Contact Form */}
          <ContactFormSection />
        </div>
      </section>
    </>
  );
}
